import React from "react";
import { HeaderAction, HeaderComponent } from "../../../types";
import { renderIcon } from "../../../utils/icons";
import VisibilitySettings from "./VisibilitySettings";

interface ButtonSettingsProps {
	component: HeaderComponent;
	dispatch: React.Dispatch<HeaderAction>;
	onBackClick: () => void;
}

const ButtonSettings: React.FC<ButtonSettingsProps> = ({
	component,
	dispatch,
	onBackClick,
}) => {
	const iconOptions = {
		wishlist: [
			{ value: "heart", label: "Heart" },
			{ value: "bell", label: "Bell" },
			{ value: "user", label: "User" },
			{ value: "settings", label: "Settings" },
		],
		cart: [
			{ value: "cart", label: "Cart" },
			{ value: "package", label: "Package" },
			{ value: "search", label: "Search" },
			{ value: "mail", label: "Mail" },
		],
		user: [
			{ value: "user", label: "User" },
			{ value: "settings", label: "Settings" },
			{ value: "heart", label: "Heart" },
			{ value: "message", label: "Message" },
		],
		menu: [
			{ value: "menu", label: "Menu" },
			{ value: "grid", label: "Grid" },
			{ value: "settings", label: "Settings" },
			{ value: "phone", label: "Phone" },
		],
	};

	// Get the appropriate icon options for this component type
	const currentIconOptions =
		iconOptions[component.type as keyof typeof iconOptions] || [];

	return (
		<div className="p-4">
			<div className="flex items-center mb-4">
				<button
					className="mr-2 p-1 rounded hover:bg-gray-100"
					onClick={onBackClick}>
					{renderIcon("menu", 16)}
				</button>
				<h3 className="text-lg font-medium">
					{component.type.charAt(0).toUpperCase() +
						component.type.slice(1)}{" "}
					Settings
				</h3>
			</div>

			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">Size</label>
				<select
					className="w-full p-2 border rounded"
					value={component.settings?.size}
					onChange={(e) =>
						dispatch({
							type: "UPDATE_COMPONENT",
							payload: {
								id: component.id,
								updates: {
									settings: {
										...component.settings,
										size: e.target.value,
									},
								},
							},
						})
					}>
					<option value="S">Small</option>
					<option value="M">Medium</option>
					<option value="L">Large</option>
					<option value="XL">Extra Large</option>
				</select>
			</div>

			{/* Shape settings */}
			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">
					Button Shape
				</label>
				<select
					className="w-full p-2 border rounded"
					value={component.settings?.shape || "rounded"}
					onChange={(e) =>
						dispatch({
							type: "UPDATE_COMPONENT",
							payload: {
								id: component.id,
								updates: {
									settings: {
										...component.settings,
										shape: e.target.value,
									},
								},
							},
						})
					}>
					<option value="rounded">Rounded</option>
					<option value="box">Box</option>
					<option value="circle">Circle</option>
				</select>
			</div>

			{/* Border radius setting */}
			{component.settings?.shape === "rounded" && (
				<div className="mb-4">
					<label className="block text-sm font-medium mb-1">
						Border Radius
					</label>
					<input
						type="range"
						min="0"
						max="20"
						value={parseInt(component.settings.borderRadius || "4")}
						onChange={(e) =>
							dispatch({
								type: "UPDATE_COMPONENT",
								payload: {
									id: component.id,
									updates: {
										settings: {
											...component.settings,
											borderRadius: e.target.value,
										},
										styles: {
											...component.styles,
											borderRadius: `${e.target.value}px`,
										},
									},
								},
							})
						}
						className="w-full"
					/>
					<div className="flex justify-between">
						<span>0px</span>
						<span>
							{parseInt(component.settings.borderRadius || "4")}px
						</span>
						<span>20px</span>
					</div>
				</div>
			)}

			{/* Container background */}
			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">
					Container Background
				</label>
				<div className="flex gap-2">
					<input
						type="color"
						value={component.settings?.background || "#ffffff"}
						onChange={(e) =>
							dispatch({
								type: "UPDATE_COMPONENT",
								payload: {
									id: component.id,
									updates: {
										settings: {
											...component.settings,
											background: e.target.value,
										},
										styles: {
											...component.styles,
											background: e.target.value,
										},
									},
								},
							})
						}
						className="w-10 h-10"
					/>
					<input
						type="text"
						className="flex-1 p-2 border rounded"
						value={component.settings?.background || "#ffffff"}
						onChange={(e) =>
							dispatch({
								type: "UPDATE_COMPONENT",
								payload: {
									id: component.id,
									updates: {
										settings: {
											...component.settings,
											background: e.target.value,
										},
										styles: {
											...component.styles,
											background: e.target.value,
										},
									},
								},
							})
						}
					/>
				</div>
			</div>

			{/* Hover color */}
			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">
					Hover Color
				</label>
				<div className="flex gap-2">
					<input
						type="color"
						value={component.settings?.hoverColor || "#f3f4f6"}
						onChange={(e) =>
							dispatch({
								type: "UPDATE_COMPONENT",
								payload: {
									id: component.id,
									updates: {
										settings: {
											...component.settings,
											hoverColor: e.target.value,
										},
									},
								},
							})
						}
						className="w-10 h-10"
					/>
					<input
						type="text"
						className="flex-1 p-2 border rounded"
						value={component.settings?.hoverColor || "#f3f4f6"}
						onChange={(e) =>
							dispatch({
								type: "UPDATE_COMPONENT",
								payload: {
									id: component.id,
									updates: {
										settings: {
											...component.settings,
											hoverColor: e.target.value,
										},
									},
								},
							})
						}
					/>
				</div>
			</div>

			{/* Hover animation */}
			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">
					Hover Animation
				</label>
				<select
					className="w-full p-2 border rounded"
					value={component.settings?.hoverAnimation || "none"}
					onChange={(e) =>
						dispatch({
							type: "UPDATE_COMPONENT",
							payload: {
								id: component.id,
								updates: {
									settings: {
										...component.settings,
										hoverAnimation: e.target.value,
									},
								},
							},
						})
					}>
					<option value="none">None</option>
					<option value="spin">Spin</option>
					<option value="pop">Pop</option>
					<option value="flash">Flash</option>
					<option value="fade">Fade</option>
				</select>
			</div>

			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">Icon</label>
				<div className="grid grid-cols-4 gap-2 mb-2">
					{currentIconOptions.map((option) => (
						<button
							key={option.value}
							className={`p-2 border rounded flex flex-col items-center ${
								component.settings?.icon === option.value
									? "border-blue-500 bg-blue-50"
									: "border-gray-300"
							}`}
							onClick={() =>
								dispatch({
									type: "UPDATE_COMPONENT",
									payload: {
										id: component.id,
										updates: {
											settings: {
												...component.settings,
												icon: option.value,
											},
										},
									},
								})
							}>
							<div className="mb-1">
								{renderIcon(option.value)}
							</div>
							<div className="text-xs">{option.label}</div>
						</button>
					))}
				</div>
			</div>

			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">
					Button Type
				</label>
				<select
					className="w-full p-2 border rounded"
					value={component.settings?.type}
					onChange={(e) =>
						dispatch({
							type: "UPDATE_COMPONENT",
							payload: {
								id: component.id,
								updates: {
									settings: {
										...component.settings,
										type: e.target.value,
									},
								},
							},
						})
					}>
					<option value="button">Button</option>
					<option value="anchor">Link</option>
				</select>
			</div>

			{component.settings?.type === "anchor" && (
				<div className="mb-4">
					<label className="block text-sm font-medium mb-1">
						Link URL
					</label>
					<input
						type="text"
						className="w-full p-2 border rounded"
						value={component.settings.link || ""}
						onChange={(e) =>
							dispatch({
								type: "UPDATE_COMPONENT",
								payload: {
									id: component.id,
									updates: {
										settings: {
											...component.settings,
											link: e.target.value,
										},
									},
								},
							})
						}
					/>
				</div>
			)}

			<VisibilitySettings component={component} dispatch={dispatch} />
		</div>
	);
};

export default ButtonSettings;
